// Browser console ring buffer for Report It (MV-1396).
//
// MytaverseLogger (helpers/logger.ts) writes straight to the console with no
// history, so a filed report has no record of what the client logged leading up
// to the incident — including the Hub / Spatial Manager version lines logged at
// startup. This module-scoped ring buffer captures each formatted log line so
// the report can ship a recent tail.
//
// In-memory by design (mirrors rtcStatsBuffer / connectionStateStore): the
// buffer is per page-lifetime, never persisted, and scrubbed of credentials/PII
// before it leaves the browser. The backend re-scrubs + caps as defence in depth.

// ~300 lines comfortably covers a session's startup + recent activity while the
// byte cap keeps the POST /reports body bounded (server cap is 128 KB).
const MAX_LINES = 300;
const MAX_OUTPUT_BYTES = 96 * 1024;

let lines: string[] = [];

/** Append one formatted log line to the ring buffer (called from the logger). */
export function recordBrowserLogLine(line: string): void {
  if (typeof line !== 'string' || line.length === 0) {
    return;
  }

  lines.push(line);

  if (lines.length > MAX_LINES) {
    lines.shift();
  }
}

/** Clear the buffer (tests / a fresh session). */
export function clearBrowserLogBuffer(): void {
  lines = [];
}

// Redact credentials/PII before the logs leave the browser. Order matters — the
// bearer/header forms run before the bare-JWT pass so the label survives. Kept
// in sync with the server-side backstop (report-schemas.ts scrubBrowserLogs).
const REDACTIONS: Array<[RegExp, string]> = [
  [/(authorization|bearer)\s+[A-Za-z0-9._~+/-]+=*/gi, '$1 [REDACTED]'],
  [/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[REDACTED_JWT]'],
  [
    /([?&](?:access_token|id_token|refresh_token|token|auth|password|secret)=)[^&\s"']+/gi,
    '$1[REDACTED]',
  ],
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[REDACTED_EMAIL]'],
];

/** Scrub credentials/PII from a block of log text (pure). */
export function scrubBrowserLogs(text: string): string {
  return REDACTIONS.reduce(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
    text,
  );
}

/**
 * The scrubbed, byte-capped log tail for a report, or undefined when empty.
 * Keeps the most recent bytes when over the cap (the tail is the incident).
 */
export function getScrubbedBrowserLogs(): string | undefined {
  if (lines.length === 0) {
    return undefined;
  }

  const scrubbed = scrubBrowserLogs(lines.join('\n'));

  if (scrubbed.length <= MAX_OUTPUT_BYTES) {
    return scrubbed;
  }

  return scrubbed.slice(scrubbed.length - MAX_OUTPUT_BYTES);
}
